import { useCallback, useEffect, useState } from "react";
import { isOffline } from "./api.js";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** true when the failure is due to being offline (navigator.onLine === false) */
  offline: boolean;
  reload: () => void;
}

/**
 * Loads from `loader` (the API). On failure it surfaces an honest error / offline state — there
 * is NO demo fallback, so screens render real loading / empty / error / offline UI instead of
 * fake data. Pass deps to re-run the loader; call `reload()` to retry.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setOffline(false);
    loader()
      .then((d) => {
        if (!alive) return;
        setData(d);
      })
      .catch((e) => {
        if (!alive) return;
        setOffline(isOffline());
        setError((e as Error).message || "読み込みに失敗しました");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  return { data, loading, error, offline, reload };
}
